// #379 — the G100 Test Console's Settings card. The factory (2026-10-21, 3.2): "the web portal
// needs to support modifying every camera setting as soon as possible". Every setting of protocol
// 6.14.4, each with the control its rule calls for, the document's value names, what the camera
// itself last reported where it differs, and the state of the sync. Only the changed fields are
// sent; the server's rules (owner_api.RULES, through the console endpoint) have the last word.
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { g100ConsoleApi } from '@/api/endpoints'
import { queryKeys, queryOptions } from '@/config/queryConfig'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { toast } from '@/lib/toast'
import { cn } from '@/lib/utils'
import { G100_SETTINGS, type G100SettingSpec } from '@/lib/g100Settings'
// #403 — the controls and helpers are shared with the customer sheet
import { Control, fieldErrors, invalid, reason, same, show, statusOf, when } from '@/components/g100/G100SettingControls'
import type { G100ConsoleCommand, G100ConsoleSettings } from '@/types'

type Values = Record<string, unknown>

/** Never faster than the page's status: a pending change can wait hours for an asleep camera, and
 *  three engineers share one account against per-user limits. The sync's progress is read from the
 *  Results query the page already polls while a command is open (see the effect below). */
export function settingsRefetchMs(_data: G100ConsoleSettings | undefined): number {
  return queryOptions.g100Console.settingsMs
}

export function SettingsCard({ cameraId }: { cameraId: number }) {
  const { t } = useTranslation('g100Console')
  const queryClient = useQueryClient()
  const settings = useQuery({
    queryKey: queryKeys.g100Console.settings(cameraId),
    queryFn: () => g100ConsoleApi.settings(cameraId),
    refetchInterval: (query) => settingsRefetchMs(query.state.data),
  })
  // The sync's progress comes from the Results query the page already keeps: when the newest
  // syncSetting row changes state (sent, done, replaced by a login reply), the set is read again.
  const commands = useQuery({
    queryKey: queryKeys.g100Console.commands(cameraId),
    queryFn: () => g100ConsoleApi.commands(cameraId),
  })
  const newestSync = commands.data?.results
    .filter((r) => r.action === 'syncSetting')
    .reduce<G100ConsoleCommand | null>((best, r) => (best === null || r.id > best.id ? r : best), null)
  const syncKey = newestSync ? `${newestSync.id}:${newestSync.state}` : ''
  // Only a change between two known rows re-reads the set: the first row seen is the baseline, and a
  // row that appears after our own send is already covered by the send's invalidation.
  const seenSync = useRef('')
  useEffect(() => {
    const previous = seenSync.current
    seenSync.current = syncKey
    if (previous && syncKey && previous !== syncKey) {
      queryClient.invalidateQueries({ queryKey: queryKeys.g100Console.settings(cameraId) })
    }
  }, [syncKey, cameraId, queryClient])

  const [draft, setDraft] = useState<Values>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const data = settings.data
  const values: Values = useMemo(() => data?.values ?? {}, [data])
  const current = (name: string) => (name in draft ? draft[name] : values[name])
  const set = (name: string, value: unknown) => {
    setDraft((d) => {
      const next = { ...d }
      if (same(value, values[name])) delete next[name]
      else next[name] = value
      return next
    })
    setErrors((e) => { const next = { ...e }; delete next[name]; return next })
  }
  const changes = useMemo(
    () => Object.fromEntries(Object.entries(draft).filter(([k, v]) => !same(v, values[k]))), [draft, values])
  const changedCount = Object.keys(changes).length
  const blocked = G100_SETTINGS.some((spec) => spec.name in changes && invalid(spec, changes[spec.name]))

  const send = useMutation({
    mutationFn: () => g100ConsoleApi.changeSettings(cameraId, changes),
    onSuccess: (saved) => {
      toast.success(t('settings.queued', { version: saved.version }))
      setDraft({})
      setErrors({})
      queryClient.invalidateQueries({ queryKey: queryKeys.g100Console.settings(cameraId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.g100Console.commands(cameraId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.g100Console.messagesAll(cameraId) })
    },
    onError: (error: unknown) => {
      if (statusOf(error) === 429) {
        toast.error(t('settings.sendFailed'), t('settings.throttled'))
        return
      }
      setErrors(fieldErrors(error))
      toast.error(t('settings.sendFailed'), reason(error))
    },
  })

  return (
    <Card>
      <section aria-label={t('settings.title')}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{t('settings.title')}</CardTitle>
          <p className="text-xs text-muted-foreground">{t('settings.subtitle')}</p>
        </CardHeader>
        <CardContent className="space-y-3">
          {settings.isLoading ? (
            <p className="text-sm text-muted-foreground">{t('loading')}</p>
          ) : settings.isError || !data ? (
            <p className="text-sm text-destructive">{t('settings.loadError')}</p>
          ) : (
            <>
              <Header data={data} />
              <ul className="divide-y divide-border/40">
                {G100_SETTINGS.map((spec) => (
                  <Row
                    key={spec.name}
                    spec={spec}
                    value={current(spec.name)}
                    changed={spec.name in changes}
                    reported={spec.name in (data.reported ?? {}) && !same(data.reported[spec.name], values[spec.name])
                      ? data.reported[spec.name] : undefined}
                    error={errors[spec.name]}
                    onChange={(v) => set(spec.name, v)}
                  />
                ))}
              </ul>
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Button size="sm" onClick={() => send.mutate()} disabled={changedCount === 0 || blocked || send.isPending}>
                  {send.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t('settings.send')}
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setDraft({}); setErrors({}) }} disabled={changedCount === 0}>
                  {t('settings.discard')}
                </Button>
                {changedCount > 0 && (
                  <span className="text-xs text-muted-foreground">{t('settings.changed', { count: changedCount })}</span>
                )}
              </div>
            </>
          )}
        </CardContent>
      </section>
    </Card>
  )
}

/** Version, sync state, where the set comes from, what the camera reported. */
function Header({ data }: { data: G100ConsoleSettings }) {
  const { t } = useTranslation('g100Console')
  const reported = data.reported ?? {}
  const hasReport = Object.keys(reported).length > 0
  let state: string
  if (data.version === 0 && data.source !== 'ours') state = t('settings.neverSent')
  else if (data.pending && data.last_sync?.state === 'superseded') state = t('settings.sentAtLogin')
  else if (data.pending) state = t('settings.waiting')
  else state = t('settings.acknowledged')
  return (
    <div className="space-y-1 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        {data.source === 'ours' && <span className="font-mono">{t('settings.version', { version: data.version })}</span>}
        <span className={cn('rounded px-1.5 py-0.5 font-medium',
          data.pending ? 'bg-amber-500/15 text-amber-700' : 'bg-muted text-muted-foreground')}>{state}</span>
        {data.pending && data.last_sync && data.last_sync.state !== 'superseded' && (
          <span className="text-muted-foreground">
            {t('settings.syncState', { state: t(`results.state.${data.last_sync.state}`) })}
          </span>
        )}
      </div>
      <p className="text-muted-foreground">{t(`settings.source.${data.source}`)}</p>
      <p className="text-muted-foreground">
        {hasReport
          ? t('settings.reportedVersion', { version: show(reported.version ?? '?'), when: when(data.reported_at) })
          : t('settings.noReport')}
      </p>
    </div>
  )
}

function Row({ spec, value, changed, reported, error, onChange }: {
  spec: G100SettingSpec
  value: unknown
  changed: boolean
  reported: unknown          // the camera's own value when it differs from the server's set
  error: string | undefined
  onChange: (value: unknown) => void
}) {
  const { t } = useTranslation('g100Console')
  const wide = spec.kind === 'windows' || spec.kind === 'species' || spec.kind === 'zone'
  // The row under the pointer and the row whose control has the focus stand out; a row with an
  // unsent change stays amber until it is sent or discarded (owner, 2026-10-23: "visually difficult").
  return (
    <li
      data-testid="g100-setting-row"
      data-changed={changed ? 'true' : undefined}
      className={cn(
        '-mx-2 rounded px-2 py-1.5 transition-colors border-l-2 border-transparent',
        'hover:bg-muted/60 focus-within:bg-muted/80 focus-within:border-primary',
        changed && 'bg-amber-500/10 border-amber-500 hover:bg-amber-500/15 focus-within:bg-amber-500/15',
      )}
    >
      <div className={cn('grid gap-x-3 gap-y-1', wide ? 'grid-cols-1' : 'grid-cols-[minmax(0,1fr)_auto] items-center')}>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span data-testid="g100-setting-name" className="font-mono text-xs font-semibold">{spec.name}</span>
            {spec.advanced && (
              <span className="rounded bg-muted px-1 py-0.5 text-[10px] uppercase text-muted-foreground">{t('settings.advanced')}</span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">{t(`settings.fields.${spec.name}`)}</p>
          {reported !== undefined && (
            <p className="text-xs text-amber-700">{t('settings.reportedValue', { value: show(reported) })}</p>
          )}
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
        <div className={cn(!wide && 'justify-self-end')}>
          <Control spec={spec} value={value} onChange={onChange} />
        </div>
      </div>
    </li>
  )
}
